<template>
  <div class="flex-1 overflow-auto p-6">
    <div class="max-w-3xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Armazenamento e Backup
        </h1>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Configure o Cloudflare R2 para armazenar backups das suas conversas.
          Cada empresa pode ter seu próprio bucket R2.
        </p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-12">
        <span class="text-slate-400">Carregando configurações...</span>
      </div>

      <template v-else>
        <!-- Status card -->
        <div
          class="mb-6 rounded-lg border p-4 flex items-center gap-3"
          :class="
            settings.active
              ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
              : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
          "
        >
          <span class="text-xl">{{ settings.active ? '✅' : '⚠️' }}</span>
          <div>
            <p
              class="font-medium"
              :class="
                settings.active
                  ? 'text-green-800 dark:text-green-300'
                  : 'text-amber-800 dark:text-amber-300'
              "
            >
              {{
                settings.active
                  ? 'Backup ativo — R2 configurado e habilitado'
                  : settings.r2_configured
                  ? 'R2 configurado, mas backup desabilitado'
                  : 'R2 não configurado'
              }}
            </p>
            <p
              class="text-sm"
              :class="
                settings.active
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-amber-600 dark:text-amber-400'
              "
            >
              {{
                settings.active
                  ? `Backup diário às ${settings.daily_backup_hour}h UTC · Retenção: ${settings.retention_days === 0 ? 'indefinida' : settings.retention_days + ' dias'}`
                  : 'Preencha as credenciais abaixo e habilite o backup.'
              }}
            </p>
          </div>
        </div>

        <!-- ── Ações Rápidas ───────────────────────────────────────────────── -->
        <div class="mb-6 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div class="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <h2 class="font-semibold text-slate-700 dark:text-slate-200">Ações Rápidas</h2>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Acione operações imediatas sem esperar pelo agendamento automático.
            </p>
          </div>
          <div class="p-4 flex flex-wrap items-center gap-4">
            <!-- Botão Backup Manual -->
            <div class="flex flex-col gap-1">
              <button
                type="button"
                :disabled="runningBackup || !settings.active"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                @click="runBackupNow"
              >
                <span>{{ runningBackup ? '⏳' : '💾' }}</span>
                {{ runningBackup ? 'Iniciando backup...' : 'Fazer Backup Agora' }}
              </button>
              <p class="text-xs text-slate-400">
                {{ settings.active ? 'Faz backup de todas as conversas marcadas imediatamente.' : 'Ative o backup para usar esta função.' }}
              </p>
            </div>

            <!-- Resultado do backup manual -->
            <div
              v-if="runNowResult"
              class="flex-1 rounded-md px-3 py-2 text-sm"
              :class="runNowResult.success
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'"
            >
              {{ runNowResult.message }}
              <router-link
                v-if="runNowResult.success"
                :to="{ name: 'backup_history', params: $route.params }"
                class="ml-2 underline font-medium"
              >
                Ver histórico →
              </router-link>
            </div>
          </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="saveSettings" class="space-y-6">
          <!-- R2 Credentials -->
          <div class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div class="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <h2 class="font-semibold text-slate-700 dark:text-slate-200">
                Credenciais Cloudflare R2
              </h2>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Encontre essas informações no painel do Cloudflare &gt; R2 &gt; Manage R2 API Tokens
              </p>
            </div>
            <div class="p-4 space-y-4">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Endpoint <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.r2_endpoint"
                    type="url"
                    placeholder="https://<account_id>.r2.cloudflarestorage.com"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Bucket <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.r2_bucket"
                    type="text"
                    placeholder="meu-bucket-viachat"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Access Key ID <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.r2_access_key_id"
                    type="text"
                    placeholder="Access Key ID"
                    autocomplete="off"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Secret Access Key <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.r2_secret_access_key"
                    :type="showSecret ? 'text' : 'password'"
                    :placeholder="settings.r2_configured ? '(mantém o atual se vazio)' : 'Secret Access Key'"
                    autocomplete="new-password"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    class="mt-1 text-xs text-blue-500 hover:underline"
                    @click="showSecret = !showSecret"
                  >
                    {{ showSecret ? 'Ocultar' : 'Mostrar' }}
                  </button>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Região
                  </label>
                  <input
                    v-model="form.r2_region"
                    type="text"
                    placeholder="auto"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p class="text-xs text-slate-400 mt-1">Use "auto" para Cloudflare R2</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    URL Pública do Bucket
                  </label>
                  <input
                    v-model="form.r2_public_url"
                    type="url"
                    placeholder="https://pub-xxx.r2.dev"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p class="text-xs text-slate-400 mt-1">URL pública para download dos arquivos</p>
                </div>
              </div>

              <!-- Test connection button -->
              <div class="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  :disabled="testingConnection"
                  class="px-4 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                  @click="testConnection"
                >
                  {{ testingConnection ? 'Testando...' : '🔌 Testar Conexão' }}
                </button>
                <span v-if="connectionStatus" :class="connectionStatus.success ? 'text-green-600' : 'text-red-600'" class="text-sm">
                  {{ connectionStatus.message }}
                </span>
              </div>
            </div>
          </div>

          <!-- Backup options -->
          <div class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div class="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <h2 class="font-semibold text-slate-700 dark:text-slate-200">Opções de Backup</h2>
            </div>
            <div class="p-4 space-y-4">
              <!-- Enable toggle -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-slate-700 dark:text-slate-200">Habilitar backup automático</p>
                  <p class="text-sm text-slate-500 dark:text-slate-400">Executa diariamente para conversas marcadas</p>
                </div>
                <button
                  type="button"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                  :class="form.enabled ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'"
                  @click="form.enabled = !form.enabled"
                >
                  <span
                    class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                    :class="form.enabled ? 'translate-x-6' : 'translate-x-1'"
                  />
                </button>
              </div>

              <hr class="border-slate-200 dark:border-slate-700" />

              <!-- What to backup -->
              <p class="text-sm font-medium text-slate-700 dark:text-slate-300">O que incluir no backup:</p>
              <div class="space-y-3">
                <label class="flex items-center gap-3 cursor-pointer">
                  <input v-model="form.backup_pdf" type="checkbox" class="rounded border-slate-300 text-blue-600" />
                  <div>
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-200">PDF formatado</span>
                    <p class="text-xs text-slate-500">Histórico visual da conversa em PDF</p>
                  </div>
                </label>
                <label class="flex items-center gap-3 cursor-pointer">
                  <input v-model="form.backup_json" type="checkbox" class="rounded border-slate-300 text-blue-600" />
                  <div>
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-200">JSON completo</span>
                    <p class="text-xs text-slate-500">Exportação completa com todos os metadados</p>
                  </div>
                </label>
                <label class="flex items-center gap-3 cursor-pointer">
                  <input v-model="form.backup_attachments" type="checkbox" class="rounded border-slate-300 text-blue-600" />
                  <div>
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-200">Arquivos anexados</span>
                    <p class="text-xs text-slate-500">Imagens, documentos e áudios enviados na conversa</p>
                  </div>
                </label>
              </div>

              <hr class="border-slate-200 dark:border-slate-700" />

              <!-- Schedule -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Horário do backup diário (UTC)
                  </label>
                  <select
                    v-model.number="form.daily_backup_hour"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option v-for="h in 24" :key="h - 1" :value="h - 1">
                      {{ String(h - 1).padStart(2, '0') }}:00 UTC
                      {{ h - 1 === 2 ? '(recomendado)' : '' }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Retenção (dias)
                  </label>
                  <input
                    v-model.number="form.retention_days"
                    type="number"
                    min="0"
                    max="3650"
                    class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p class="text-xs text-slate-400 mt-1">0 = manter para sempre</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Error / success -->
          <div v-if="saveError" class="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
            {{ saveError }}
          </div>
          <div v-if="saveSuccess" class="rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 text-sm text-green-700 dark:text-green-300">
            Configurações salvas com sucesso!
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-between pt-2">
            <router-link
              :to="{ name: 'backup_history', params: $route.params }"
              class="text-sm text-blue-600 hover:underline"
            >
              Ver histórico de backups →
            </router-link>
            <button
              type="submit"
              :disabled="saving"
              class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
            >
              {{ saving ? 'Salvando...' : 'Salvar configurações' }}
            </button>
          </div>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
import backupAPI from '../../../../api/backup';

export default {
  name: 'BackupSettingsPage',
  data() {
    return {
      loading: true,
      saving: false,
      testingConnection: false,
      runningBackup: false,
      showSecret: false,
      saveError: null,
      saveSuccess: false,
      connectionStatus: null,
      runNowResult: null,
      settings: {},
      form: {
        r2_endpoint: '',
        r2_access_key_id: '',
        r2_secret_access_key: '',
        r2_bucket: '',
        r2_region: 'auto',
        r2_public_url: '',
        enabled: false,
        backup_attachments: true,
        backup_pdf: true,
        backup_json: true,
        daily_backup_hour: 2,
        retention_days: 90,
      },
    };
  },
  async created() {
    await this.loadSettings();
  },
  methods: {
    async loadSettings() {
      try {
        const { data } = await backupAPI.getSettings();
        this.settings = data;
        this.form = {
          r2_endpoint:          data.r2_endpoint || '',
          r2_access_key_id:     data.r2_access_key_id || '',
          r2_secret_access_key: '', // never pre-fill secret
          r2_bucket:            data.r2_bucket || '',
          r2_region:            data.r2_region || 'auto',
          r2_public_url:        data.r2_public_url || '',
          enabled:              data.enabled || false,
          backup_attachments:   data.backup_attachments !== false,
          backup_pdf:           data.backup_pdf !== false,
          backup_json:          data.backup_json !== false,
          daily_backup_hour:    data.daily_backup_hour ?? 2,
          retention_days:       data.retention_days ?? 90,
        };
      } catch (e) {
        // settings not yet created — use defaults
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.saving = true;
      this.saveError = null;
      this.saveSuccess = false;
      try {
        const payload = { ...this.form };
        // Don't send empty secret (would clear it)
        if (!payload.r2_secret_access_key) delete payload.r2_secret_access_key;
        const { data } = await backupAPI.updateSettings(payload);
        this.settings = data;
        this.saveSuccess = true;
        setTimeout(() => { this.saveSuccess = false; }, 3000);
      } catch (e) {
        this.saveError = e.response?.data?.errors?.join(', ') || 'Erro ao salvar configurações.';
      } finally {
        this.saving = false;
      }
    },
    async testConnection() {
      this.testingConnection = true;
      this.connectionStatus = null;
      try {
        const { data } = await backupAPI.testConnection();
        this.connectionStatus = { success: true, message: data.message || 'Conexão OK!' };
      } catch (e) {
        const msg = e.response?.data?.error || 'Falha na conexão.';
        this.connectionStatus = { success: false, message: msg };
      } finally {
        this.testingConnection = false;
      }
    },
    async runBackupNow() {
      this.runningBackup = true;
      this.runNowResult = null;
      try {
        const { data } = await backupAPI.runNow();
        this.runNowResult = {
          success: true,
          message: data.message || `Backup iniciado para ${data.queued_count} conversa(s).`,
        };
        // Auto-clear after 10 seconds
        setTimeout(() => { this.runNowResult = null; }, 10000);
      } catch (e) {
        const msg = e.response?.data?.error || 'Erro ao iniciar backup manual.';
        this.runNowResult = { success: false, message: msg };
      } finally {
        this.runningBackup = false;
      }
    },
  },
};
</script>
